// pages/ChatSpace/ChatSpace.js
import React, { useState } from 'react';
import {
    Box,
    IconButton,
    Typography,
    Switch,
    FormControlLabel,
    Tooltip,
    useMediaQuery,
    useTheme,
    CircularProgress,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ForumIcon from '@mui/icons-material/Forum';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import ShareIcon from '@mui/icons-material/Share';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import { useNavigate, useParams } from 'react-router-dom';
import { useChatbots } from '../../../contexts/ChatbotContext';
import ChatSpaceSidebar from './ChatSpaceSidebar';
import ChatSpaceInterface from './ChatSpaceInterface';
import ChatSpaceCitations from './ChatSpaceCitations';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#3b82f6',
};

export default function ChatSpace() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const navigate = useNavigate();
    const { chatbotId } = useParams();
    const { activeChatbot } = useChatbots();

    // Layout state
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // Citations state
    const [citations, setCitations] = useState([]);
    const [sourcesOpen, setSourcesOpen] = useState(false);
    const [sourcesCollapsed, setSourcesCollapsed] = useState(false);

    // Chat state
    const [currentSessionCode, setCurrentSessionCode] = useState(null);

    // Privacy state
    const [privacy, setPrivacy] = useState(true);

    const hasCitations = citations && citations.length > 0;

    const handleCitationsChange = (newCitations) => {
        setCitations(newCitations);
        if (newCitations.length > 0) {
            setSourcesOpen(true);
            setSourcesCollapsed(false);
        }
    };

    const handleCloseCitations = () => {
        setCitations([]);
        setSourcesOpen(false);
    };

    const handleConversationSelect = (sessionCode) => {
        if (!sessionCode) {
            setCurrentSessionCode(null);
        } else {
            setCurrentSessionCode(sessionCode);
        }
        setCitations([]);
        setSourcesOpen(false);
        setSidebarOpen(false);
    };

    return (
        <Box sx={{ fontFamily, maxWidth: '1200px', width: '100%', mx: 'auto' }}>

            {/* Toolbar */}
            <Box sx={{
                bgcolor: 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(0,0,0,0.06)',
                borderRadius: { xs: '10px', sm: '14px' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: { xs: 1.5, sm: 2.5 },
                py: { xs: 0.75, sm: 1 },
                mb: { xs: 1, sm: 2 },
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {isMobile && (
                        <IconButton
                            size="small"
                            onClick={() => setSidebarOpen(true)}
                            sx={{ color: colors.textTertiary, '&:hover': { bgcolor: 'rgba(59,130,246,0.1)', color: colors.accent } }}
                        >
                            <MenuIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    )}
                    <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: colors.textSecondary }}>
                        {currentSessionCode ? 'Active Conversation' : 'New Conversation'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                    <Tooltip title="Distribute Chat Space">
                        <IconButton
                            size="small"
                            onClick={() => navigate(`/chatbot/${chatbotId}/chat-space/distribute`)}
                            sx={{ color: colors.textTertiary, '&:hover': { bgcolor: 'rgba(59,130,246,0.1)', color: colors.accent } }}
                        >
                            <ShareIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={privacy}
                                onChange={(e) => setPrivacy(e.target.checked)}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: colors.accent },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.accent },
                                }}
                            />
                        }
                        label={<Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary, display: { xs: 'none', sm: 'block' } }}>Privacy</Typography>}
                        sx={{ ml: 0, mr: 0 }}
                    />
                    {hasCitations && (
                        <IconButton
                            size="small"
                            onClick={() => isMobile ? setSourcesOpen(!sourcesOpen) : setSourcesCollapsed(!sourcesCollapsed)}
                            sx={{ color: colors.textTertiary, '&:hover': { bgcolor: 'rgba(59,130,246,0.1)', color: colors.accent } }}
                        >
                            <ViewSidebarIcon sx={{ fontSize: 20, transform: sourcesCollapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Chat Area */}
            <Box sx={{ display: 'flex', width: '100%', overflow: 'hidden', gap: { xs: 0, lg: 2 } }}>

                {/* LEFT SIDEBAR */}
                {!isMobile && (
                    <ChatSpaceSidebar
                        isOpen={true}
                        isCollapsed={sidebarCollapsed}
                        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
                        onConversationSelect={handleConversationSelect}
                        currentSessionCode={currentSessionCode}
                    />
                )}
                {isMobile && (
                    <ChatSpaceSidebar
                        isOpen={sidebarOpen}
                        isCollapsed={false}
                        onToggle={() => setSidebarOpen(!sidebarOpen)}
                        onConversationSelect={handleConversationSelect}
                        currentSessionCode={currentSessionCode}
                        isMobile
                    />
                )}

                {/* MAIN CHAT */}
                <Box sx={{ flex: 1, overflow: 'hidden', borderRadius: '16px' }}>
                    <ChatSpaceInterface
                        sessionCode={currentSessionCode}
                        onSessionChange={setCurrentSessionCode}
                        onCitationsChange={handleCitationsChange}
                        privacy={privacy}
                    />
                </Box>

                {/* RIGHT SIDEBAR - Citations */}
                {hasCitations && !isMobile && (
                    <Box sx={{
                        width: sourcesCollapsed ? 56 : 240,
                        flexShrink: 0,
                        transition: 'width 0.3s ease',
                        height: '100%',
                        bgcolor: 'rgba(255,255,255,0.7)',
                        backdropFilter: 'blur(12px)',
                        borderRadius: '16px',
                        border: '1px solid rgba(0,0,0,0.06)',
                        overflow: 'hidden',
                    }}>
                        <ChatSpaceCitations
                            isOpen={true}
                            isCollapsed={sourcesCollapsed}
                            onToggle={() => setSourcesCollapsed(!sourcesCollapsed)}
                            onClose={handleCloseCitations}
                            citations={citations}
                        />
                    </Box>
                )}
                {hasCitations && isMobile && (
                    <ChatSpaceCitations
                        isOpen={sourcesOpen}
                        isCollapsed={false}
                        onToggle={() => setSourcesOpen(!sourcesOpen)}
                        onClose={() => { setSourcesOpen(false); handleCloseCitations(); }}
                        citations={citations}
                    />
                )}
            </Box>
        </Box>
    );
}
